// qs-useCustomImageUpload.ts
import type { CustomImageData } from '@/types/qs-type-customImage'
import { CUSTOM_IMAGE_ID } from '@/types/qs-type-customImage'
import {
    openFileDialog,
    processImageFile,
    validateImageFile
} from '@/utils/qs-util-imageProcessor'
import { useStorage } from '@vueuse/core'
import { computed, ref } from 'vue'

export function useCustomImageUpload() {
  const isUploading = ref(false)
  const isFileDialogOpen = ref(false)
  const uploadError = ref<string | null>(null)
  const customImageData = useStorage<CustomImageData | null>('silhouette-custom-image', null)

  const hasCustomImage = computed(() => customImageData.value !== null)
  const isCustomImageActive = computed(() => hasCustomImage.value)

  const selectCustomImage = async (): Promise<boolean> => {
    if (isUploading.value || isFileDialogOpen.value) return false

    try {
      isFileDialogOpen.value = true
      uploadError.value = null

      const file = await openFileDialog()
      isFileDialogOpen.value = false

      if (!file) return false

      isUploading.value = true

      const validation = validateImageFile(file)
      if (!validation.isValid) {
        uploadError.value = validation.error || 'Invalid file'
        return false
      }

      const processedData = await processImageFile(file)

      const customImage: CustomImageData = {
        id: CUSTOM_IMAGE_ID,
        name: file.name,
        dataUrl: processedData.dataUrl,
        preview: processedData.preview,
        originalDimensions: processedData.originalDimensions,
        processedDimensions: processedData.processedDimensions,
        fileSize: file.size,
        fileType: file.type,
        createdAt: new Date()
      }

      customImageData.value = customImage
      return true
    } catch (error) {
      uploadError.value = error instanceof Error ? error.message : 'Upload failed'
      return false
    } finally {
      isUploading.value = false
      isFileDialogOpen.value = false
    }
  }

  const removeCustomImage = () => {
    customImageData.value = null
    uploadError.value = null
  }

  const getCustomImageAsSilhouette = () => {
    if (!customImageData.value) return null

    return {
      id: customImageData.value.id,
      name: `Custom: ${customImageData.value.name}`,
      filename: customImageData.value.name,
      preview: customImageData.value.preview,
      path: customImageData.value.dataUrl,
      isCustom: true,
      originalDimensions: customImageData.value.originalDimensions,
      category: 'custom' as const
    }
  }

  const clearError = () => {
    uploadError.value = null
  }

  const getUploadStatus = () => {
    if (isFileDialogOpen.value) return 'selecting'
    if (isUploading.value) return 'uploading'
    if (uploadError.value) return 'error'
    if (hasCustomImage.value) return 'success'
    return 'idle'
  }

  const validateCustomImage = (): boolean => {
    if (!customImageData.value) return false

    const required: (keyof CustomImageData)[] = ['id', 'dataUrl', 'preview', 'processedDimensions']
    return required.every(field => customImageData.value![field] !== undefined)
  }

  const getFileInfo = () => {
    if (!customImageData.value) return null

    return {
      name: customImageData.value.name,
      size: `${Math.round(customImageData.value.fileSize / 1024)}KB`,
      dimensions: `${customImageData.value.originalDimensions.width}×${customImageData.value.originalDimensions.height}`,
      type: customImageData.value.fileType.split('/')[1].toUpperCase()
    }
  }

  const cleanup = () => {
    isUploading.value = false
    isFileDialogOpen.value = false
    uploadError.value = null
  }

  return {
    isUploading,
    isFileDialogOpen,
    uploadError,
    customImageData,
    hasCustomImage,
    isCustomImageActive,
    selectCustomImage,
    removeCustomImage,
    getCustomImageAsSilhouette,
    clearError,
    getUploadStatus,
    validateCustomImage,
    getFileInfo,
    cleanup
  }
}